import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import MembershipSwitcher from '../MembershipSwitcher';
import GoogleTranslate from '../common/GoogleTranslate';
import { directionsUrl, GoogleLocationMap, normalizeLocation } from '../maps/LocationMap';
import GovernmentLogoMarquee from './GovernmentLogoMarquee';
import { phoneHref } from '../../utils/phone';
import './PublicLayout.css';
import './PublicTheme.css';
import './CitizenResponsive.css';

const DEFAULT_GP_LOGO = '/certificate-assets/ashoka-emblem.png';
const useDefaultLogoOnError = event => {
  if (event.currentTarget.src.endsWith(DEFAULT_GP_LOGO)) return;
  event.currentTarget.src = DEFAULT_GP_LOGO;
};

const Icon = ({ name, size = 18 }) => {
  const paths = {
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    user: <><circle cx="12" cy="7" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
    close: <><path d="m6 6 12 12M18 6 6 18" /></>,
    phone: <><path d="M6.5 3h3l1.5 5-2 1.5a15 15 0 0 0 5.5 5.5l1.5-2 5 1.5v3A3.5 3.5 0 0 1 17.5 21C9.5 21 3 14.5 3 6.5A3.5 3.5 0 0 1 6.5 3Z" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" /></>,
    chevron: <><path d="m9 18 6-6-6-6" /></>,
    facebook: <path d="M14 21v-8h3l.5-4H14V7c0-1.2.4-2 2-2h2V1.5c-.8-.1-1.8-.2-3-.2-3 0-5 1.8-5 5.2V9H7v4h3v8" />,
    instagram: <><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r=".7" fill="currentColor" stroke="none" /></>,
    youtube: <><path d="M21 12c0 3-.4 5-1 5.7-.7.8-2.8 1-8 1s-7.3-.2-8-1C3.4 17 3 15 3 12s.4-5 1-5.7c.7-.8 2.8-1 8-1s7.3.2 8 1c.6.7 1 2.7 1 5.7Z" /><path d="m10 9 5 3-5 3Z" /></>,
    twitter: <><path d="M4 4 20 20M20 4 4 20" /><path d="M8.5 4H4l11.5 16H20Z" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};


const safeExternalUrl = value => {
  try {
    const url = new URL(String(value || '').trim());
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
};

export default function PublicLayout() {
  const { tenantSlug } = useParams();
  const { selectTenant, tenantData } = useTenant();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (tenantSlug) selectTenant(tenantSlug);
  }, [tenantSlug, selectTenant]);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const tenant = tenantData?.tenant;
  const customHeroStyles = useMemo(() => {
    const images = tenant?.website_theme?.page_hero_images || {};
    const styles = {};
    for (const [key, url] of Object.entries(images)) {
      if (url && typeof url === 'string' && url.trim()) {
        styles[`--custom-hero-${key}`] = `url("${url.trim()}")`;
      }
    }
    return styles;
  }, [tenant?.website_theme?.page_hero_images]);

  const builtInSlugs = new Set(['home', 'about', 'services', 'taxes', 'my-taxes', 'schemes', 'projects', 'departments', 'gallery', 'contact', 'notice', 'notices', 'complaints', 'representatives', 'banners', 'facilities', 'businesses', 'my-businesses', 'staff', 'gramsabha']);
  const customNavItems = (tenantData?.pages || [])
    .filter(page => page.is_published && !builtInSlugs.has(page.slug))
    .map(page => ({ path: `/page/${page.slug}`, label: page.title }));
  const navItems = [
    { path: '', label: 'मुख्यपृष्ठ' },
    { path: '/about', label: 'आमच्याबद्दल' },
    { path: '/departments', label: 'विभाग' },
    { path: '/projects', label: 'विकास कामे' },
    { path: '/schemes', label: 'योजना' },
    { path: '/gramsabha', label: 'ग्रामसभा' },
    { path: '/services', label: 'सेवा' },
    { path: '/my-taxes', label: 'कर' },
    { path: '/complaints', label: 'तक्रार' },
    { path: '/notices', label: 'नोटीस' },
    { path: '/gallery', label: 'गॅलरी' },
    { path: '/facilities', label: 'सुविधा' },
    { path: '/staff', label: 'कर्मचारी' },
    { path: '/businesses', label: 'व्यवसाय' },
    { path: '/contact', label: 'संपर्क' },
    ...customNavItems,
  ];
  const gpName = tenant?.name || 'देऊळगाव गाडा';
  const locationText = [tenant?.taluka && `ता. ${tenant.taluka}`, tenant?.district && `जि. ${tenant.district}`, tenant?.state].filter(Boolean).join(', ') || 'ता. दौंड, जि. पुणे, महाराष्ट्र';
  const userDisplayName = user?.full_name || user?.name || user?.username || user?.email || 'माझे खाते';
  const shortGpName = gpName.replace(/^ग्रामपंचायत\s*/i, '').replace(/\s*\(.*?\)\s*/g, '');
  const officeAddress = tenant?.address || `ग्रामपंचायत कार्यालय, ${shortGpName}, ${locationText}`;
  const officePosition = normalizeLocation(tenant?.latitude, tenant?.longitude);
  const officeDirections = directionsUrl(tenant?.latitude, tenant?.longitude, officeAddress);
  const configuredSocialLinks = [
    { key: 'facebook', label: 'Facebook', icon: 'facebook' },
    { key: 'instagram', label: 'Instagram', icon: 'instagram' },
    { key: 'youtube', label: 'YouTube', icon: 'youtube' },
    { key: 'twitter', label: 'X (Twitter)', icon: 'twitter' },
  ].map(item => ({ ...item, url: safeExternalUrl(tenant?.website_theme?.social_links?.[item.key]) }))
    .filter(item => item.url);
  const portalBasePath = `/gp/${tenantSlug}`;
  const portalPath = location.pathname.startsWith(portalBasePath)
    ? location.pathname.slice(portalBasePath.length) || '/'
    : location.pathname;
  const isCitizenDashboard = user?.role === 'citizen' && portalPath === '/dashboard';
  const isNavItemActive = item => {
    if (item.path === '') return portalPath === '/';
    if (portalPath === item.path || portalPath.startsWith(`${item.path}/`)) return true;

    const relatedRoutes = {
      '/services': ['/apply-certificate', '/certificate/', '/verify'],
      '/my-taxes': ['/taxes'],
      '/businesses': ['/my-businesses'],
    };
    return (relatedRoutes[item.path] || []).some(route => (
      portalPath === route || portalPath.startsWith(route)
    ));
  };


  return (
    <div className="public-layout" style={customHeroStyles}>
      <header className="public-header">
        <div className="header-utility">
          <div className="public-container utility-inner">
            <span className="utility-location"><Icon name="pin" size={16} /> {gpName}, {locationText}</span>
            <div className="utility-actions">
              <GoogleTranslate />
              {user ? (
                <>
                  <MembershipSwitcher compact />
                  <Link className={`utility-action utility-account utility-user-name${isCitizenDashboard ? ' active' : ''}`} aria-current={isCitizenDashboard ? 'page' : undefined} title={userDisplayName} to={user.role === 'citizen' ? `/gp/${tenantSlug}/dashboard` : `/admin/${tenantSlug}/profile`}><Icon name="user" size={15} /><span>{userDisplayName}</span></Link>
                  <Link className="utility-action utility-account utility-business" to={`/gp/${tenantSlug}/my-businesses`}>
                    <span className="utility-label-full">व्यवसाय खाते / Business account</span>
                    <span className="utility-label-short" aria-hidden="true">व्यवसाय</span>
                  </Link>
                  <button type="button" className="utility-action utility-logout" onClick={() => { logout(); navigate('/login'); }}>
                    <span className="utility-label-full">बाहेर पडा</span>
                    <span className="utility-label-short" aria-hidden="true">बाहेर</span>
                    <span aria-hidden="true">→</span>
                  </button>
                </>
              ) : (
                <>
                  <Link className="utility-action utility-business-access" to={`/gp/${tenantSlug}/register?type=business`}>
                    <span className="utility-label-full">व्यवसाय कर खाते</span>
                    <span className="utility-label-short" aria-hidden="true">व्यवसाय खाते</span>
                  </Link>
                  <Link className="utility-action utility-signup" to={`/gp/${tenantSlug}/register`}>नवीन खाते</Link>
                  <Link className="utility-action utility-account" to={`/login?gp=${tenantSlug}`}><Icon name="user" size={15} /> लॉगिन</Link>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="header-main">
          <div className="public-container header-main-inner">
            <Link to={`/gp/${tenantSlug}`} className="header-brand">
              <img
                src={tenant?.logo_url || DEFAULT_GP_LOGO}
                alt={`${gpName} लोगो`}
                className="header-logo"
                onError={useDefaultLogoOnError}
              />
              <div>
                <strong title={shortGpName}>{shortGpName}</strong>
                <span>{locationText}</span>
              </div>
            </Link>

            <nav className={`public-nav ${menuOpen ? 'open' : ''}`}>
              {navItems.map(item => (
                <NavLink key={item.path} end={item.path === ''} to={`/gp/${tenantSlug}${item.path}`} className={() => isNavItemActive(item) ? 'active' : ''}>
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="header-tools">
              <button className="menu-button" onClick={() => setMenuOpen(v => !v)} aria-label="मेनू"><Icon name={menuOpen ? 'close' : 'menu'} size={23} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="public-main">
        <div className="public-container"><Outlet /></div>
      </main>

      <GovernmentLogoMarquee />

      <footer className="public-footer">
        <div className="public-container footer-main">
          <section className="footer-intro" aria-labelledby="footer-gp-name">
            <div className="footer-brand">
              <img src={tenant?.logo_url || DEFAULT_GP_LOGO} alt={`${gpName} लोगो`} onError={useDefaultLogoOnError} />
              <div>
                <p className="footer-kicker">अधिकृत ग्रामपंचायत संकेतस्थळ</p>
                <h2 id="footer-gp-name">ग्रामपंचायत {shortGpName}</h2>
              </div>
            </div>
            <p className="footer-summary">नागरिक सेवा, ग्रामविकासाची माहिती, कर भरणा आणि ग्रामपंचायतीशी थेट संपर्क एकाच ठिकाणी.</p>
            {configuredSocialLinks.length > 0 ? (
              <div className="social-row" aria-label="ग्रामपंचायतीची सामाजिक माध्यमे">
                {configuredSocialLinks.map(item => (
                  <a key={item.key} href={item.url} target="_blank" rel="noopener noreferrer" aria-label={`${item.label} वर ग्रामपंचायत पहा`} title={item.label}>
                    <Icon name={item.icon} size={19} />
                  </a>
                ))}
              </div>
            ) : <p className="footer-social-empty">अधिकृत सामाजिक माध्यम दुवे उपलब्ध नाहीत.</p>}
          </section>

          <nav className="footer-nav" aria-label="Footer navigation">
            <h3>त्वरित दुवे</h3>
            <div className="footer-links">
              {navItems.slice(0, 8).map(item => <Link key={item.path} to={`/gp/${tenantSlug}${item.path}`}><Icon name="chevron" size={13} />{item.label}</Link>)}
            </div>
          </nav>

          <section className="footer-office" aria-labelledby="footer-contact-heading">
            <h3 id="footer-contact-heading">कार्यालय व संपर्क</h3>
            {officeDirections ? (
              <a className="footer-contact footer-contact-link" href={officeDirections} target="_blank" rel="noopener noreferrer"><Icon name="pin" />{officeAddress}</a>
            ) : <p className="footer-contact"><Icon name="pin" />{officeAddress}</p>}
            {tenant?.contact_phone && <a className="footer-contact footer-contact-link" href={phoneHref(tenant.contact_phone)}><Icon name="phone" />{tenant.contact_phone}</a>}
            {tenant?.contact_email && <a className="footer-contact footer-contact-link" href={`mailto:${tenant.contact_email}`}><Icon name="mail" />{tenant.contact_email}</a>}
            <p className="footer-contact footer-hours"><Icon name="clock" /> <span><strong>कार्यालयीन वेळ</strong>{tenant?.office_hours || 'सोमवार ते शनिवार, सकाळी १०:०० ते सायंकाळी ५:००'}</span></p>
            <Link to={`/gp/${tenantSlug}/contact`} className="footer-cta">संपर्क पृष्ठ उघडा <span aria-hidden="true">→</span></Link>
          </section>

          <section className="footer-map-panel" aria-labelledby="footer-map-heading">
            <div className="footer-map-heading">
              <div><p className="footer-kicker">कार्यालयाचे स्थान</p><h3 id="footer-map-heading">नकाशावर ग्रामपंचायत</h3></div>
              {officeDirections && <a href={officeDirections} target="_blank" rel="noopener noreferrer">दिशा मिळवा ↗</a>}
            </div>
            {officePosition ? (
              <GoogleLocationMap latitude={tenant.latitude} longitude={tenant.longitude} title={`ग्रामपंचायत ${shortGpName}`} address={officeAddress} compact />
            ) : (
              <div className="footer-map-empty"><Icon name="pin" size={28} /><strong>कार्यालयाचे नकाशावरील स्थान अद्याप नोंदवलेले नाही.</strong><span>पत्त्याच्या आधारे दिशा पाहण्यासाठी “दिशा मिळवा” वापरा.</span></div>
            )}
          </section>
        </div>

        <div className="footer-resources">
          <div className="public-container footer-resources-inner">
            <strong>महत्त्वाचे शासकीय दुवे</strong>
            <a href="https://www.maharashtra.gov.in/" target="_blank" rel="noopener noreferrer">महाराष्ट्र शासन ↗</a>
            <a href="https://panchayat.gov.in/" target="_blank" rel="noopener noreferrer">पंचायत राज मंत्रालय ↗</a>
            <a href="https://rdd.maharashtra.gov.in/" target="_blank" rel="noopener noreferrer">ग्रामविकास विभाग ↗</a>
          </div>
        </div>
        <nav className="footer-legal-links" aria-label="Legal and website policies">
          <Link to={`/gp/${tenantSlug}/legal/terms`}>Terms &amp; Conditions</Link>
          <Link to={`/gp/${tenantSlug}/legal/privacy`}>Privacy Policy</Link>
          <Link to={`/gp/${tenantSlug}/legal/refund`}>Refund Policy</Link>
          <Link to={`/gp/${tenantSlug}/legal/data-deletion`}>Data Deletion</Link>
          <Link to={`/gp/${tenantSlug}/legal/cookies`}>Cookies</Link>
          <Link to={`/gp/${tenantSlug}/legal/accessibility`}>Accessibility</Link>
          <Link to={`/gp/${tenantSlug}/legal/copyright`}>Copyright</Link>
          <Link to={`/gp/${tenantSlug}/legal/hyperlinks`}>Hyperlinking</Link>
          <Link to={`/gp/${tenantSlug}/legal/disclaimer`}>Disclaimer</Link>
        </nav>
        <div className="footer-bottom public-container">
          <span>© {new Date().getFullYear()} ग्रामपंचायत {shortGpName}. सर्व हक्क राखीव.</span>
          <a href="https://deepak.ndtechspace.com" target="_blank" rel="noopener noreferrer">
            Designed &amp; Developed by NDTechSpace
          </a>
        </div>
      </footer>
    </div>
  );
}
